import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, RefreshCw, Loader2, MessageSquare, Trophy } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import Rail from '../components/discover/Rail';
import HeroCarousel from '../components/discover/HeroCarousel';
import RailSkeleton from '../components/discover/RailSkeleton';
import WriterSpotlight from '../components/WriterSpotlight';

const MOOD_DEFS = [
  { key: 'cosy',       label: 'Cosy',       icon: '☕', tone: 'border-amber-500/50 text-amber-200 bg-amber-500/10' },
  { key: 'edge',       label: 'Edge',       icon: '🔥', tone: 'border-red-500/50 text-red-200 bg-red-500/10' },
  { key: 'dreamy',     label: 'Dreamy',     icon: '☁',  tone: 'border-indigo-500/50 text-indigo-200 bg-indigo-500/10' },
  { key: 'heartbreak', label: 'Heartbreak', icon: '💔', tone: 'border-rose-500/50 text-rose-200 bg-rose-500/10' },
];

const MoodChipStrip = ({ rails }) => {
  // Only show chips for moods that have an actual rail in the current feed.
  const present = new Set();
  (rails || []).forEach((r) => {
    if (r?.id?.startsWith('mood:')) present.add(r.id.slice(5));
  });
  if (present.size === 0) return null;

  const jump = (key) => {
    const el = document.querySelector(`[data-testid="rail-mood:${key}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="px-4 sm:px-6 pb-3 -mt-1" data-testid="mood-chip-strip">
      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5 font-bold">Today I'm in the mood for…</p>
      <div className="flex gap-2 overflow-x-auto">
        {MOOD_DEFS.filter((m) => present.has(m.key)).map((m) => (
          <button
            key={m.key}
            onClick={() => jump(m.key)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${m.tone} hover:scale-105 transition-transform`}
            data-testid={`mood-chip-${m.key}`}
          >
            <span>{m.icon}</span> {m.label}
          </button>
        ))}
      </div>
    </div>
  );
};
import GreetingStrip from '../components/discover/GreetingStrip';
import ResumeCard from '../components/discover/ResumeCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_FILTERS = [
  { id: 'all', label: 'All', match: null },
  { id: 'cinematic', label: 'Cinema', match: 'cinematic' },
  { id: 'audiobook', label: 'Audio', match: 'audiobook' },
  { id: 'ebook', label: 'Books', match: 'ebook' },
];

/**
 * Discover page — Pocket-FM-style multi-rail home feed.
 *
 * Layout:
 *  1. Hero carousel (auto-rotating spotlight, the big visual punch)
 *  2. Sticky filter chips: All / Cinema / Audio / Books
 *  3. The rails returned by /api/discover/feed, filtered by chip
 *
 * The feed is fetched once; the chip filter is applied client-side so
 * switching tabs is instant (no extra requests). Rails that have zero
 * matching items after filtering are hidden so the page stays clean.
 */
const Discover = () => {
  const navigate = useNavigate();
  const [rails, setRails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [savedSet, setSavedSet] = useState(new Set());

  const loadSaved = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/library/saved`);
      if (r.ok) {
        const data = await r.json();
        setSavedSet(new Set(data.ids || []));
      }
    } catch {}
  }, []);

  const onSaveChange = (refId, isSaved) => {
    setSavedSet((prev) => {
      const next = new Set(prev);
      if (isSaved) next.add(refId);
      else next.delete(refId);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/discover/feed`);
      if (!r.ok) throw new Error(`Feed ${r.status}`);
      const data = await r.json();
      setRails(data.rails || []);
    } catch (err) {
      setError(err.message || 'Could not load discover feed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadSaved();
  }, [load, loadSaved]);

  // Apply the type filter client-side. The Trending Authors rail is exempt
  // because it isn't tied to a specific content type.
  const filteredRails = useMemo(() => {
    if (filter === 'all') return rails;
    const targetType = TYPE_FILTERS.find((f) => f.id === filter)?.match;
    if (!targetType) return rails;
    return rails
      .map((r) => {
        if (r.kind === 'authors') return null;
        const items = (r.items || []).filter((i) => (i.type || '') === targetType);
        if (items.length === 0) return null;
        return { ...r, items };
      })
      .filter(Boolean);
  }, [rails, filter]);

  const onSeeAll = (rail) => {
    if (rail.id?.startsWith('genre:')) {
      navigate(`/library?genre=${encodeURIComponent(rail.title)}`);
    } else {
      navigate('/library');
    }
  };

  return (
    <div className="sharp-bg min-h-screen pb-28 pt-3 sm:pt-4" data-testid="discover-page">
      {/* Top bar */}
      <div className="px-4 sm:px-6 mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="eyebrow text-amber-300">Browse the catalog</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight leading-none mt-1">
            Discover<span className="text-amber-300">.</span>
          </h1>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs text-slate-400 hover:text-white flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-700 hover:border-slate-500 disabled:opacity-50 transition-colors"
          data-testid="discover-refresh-btn"
        >
          {loading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
          Refresh
        </button>
      </div>

      {/* Personalized greeting + stats — renders nothing if user has no
          activity yet so it doesn't dump empty space onto first-time
          users. */}
      <GreetingStrip />

      {/* Loading skeleton — the page reserves its real layout so the hero
          and rails don't jump when data arrives. */}
      {loading && rails.length === 0 && <RailSkeleton heroes={1} />}

      {error && !loading && (
        <div
          className="mx-4 sm:mx-6 p-6 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm"
          data-testid="discover-error"
        >
          {error}
          <button
            onClick={load}
            className="ml-3 underline hover:no-underline"
            data-testid="discover-retry-btn"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && rails.length === 0 && (
        <div
          className="px-6 py-16 text-center text-slate-400 text-sm"
          data-testid="discover-empty"
        >
          <Sparkles className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="font-medium text-slate-200">Nothing to discover yet.</p>
          <p className="mt-1">Upload some content and check back — your rails will fill up automatically.</p>
        </div>
      )}

      {!loading && rails.length > 0 && (
        <>
          {/* Hero is built off the *unfiltered* rails so it always shows
              the platform's strongest cards regardless of chip selection. */}
          <ResumeCard rails={rails} />
          {/* Scribe of the Week — sits ABOVE Pick-of-the-Week so the human
              behind the catalogue gets framed first. Hides silently when
              there's no eligible writer yet. Feb 22 2026. */}
          <WriterSpotlight />
          <HeroCarousel rails={rails} />

          {/* Sticky filter chips. `top-0` works because the AppShell's top
              bar is fixed-position outside this scroll container; mobile
              users still get the chips pinned above the rails as they
              scroll. */}
          <div className="sticky top-0 z-20 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/60 mb-5">
            <div
              className="flex gap-2 overflow-x-auto px-4 sm:px-6 py-3"
              data-testid="discover-type-filter"
            >
              {TYPE_FILTERS.map((f) => {
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    data-testid={`filter-chip-${f.id}`}
                    className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      active
                        ? 'bg-white text-slate-900 shadow-md'
                        : 'bg-slate-800/70 text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            {/* Mood quick-jump chips — only render when at least one mood
                rail exists in the current rails (server only emits these
                when ≥1 item is mood-tagged). */}
            <MoodChipStrip rails={rails} />
          </div>

          {filteredRails.length === 0 ? (
            <div
              className="px-6 py-12 text-center text-slate-500 text-sm"
              data-testid="discover-filter-empty"
            >
              No {TYPE_FILTERS.find((f) => f.id === filter)?.label.toLowerCase()} content yet.
            </div>
          ) : (
            <div className="space-y-8 sm:space-y-10">
              {filteredRails.map((rail, i) => (
                <Rail
                  key={rail.id}
                  rail={rail}
                  onSeeAll={onSeeAll}
                  savedSet={savedSet}
                  onSaveChange={onSaveChange}
                  animationDelay={i * 80}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Floating Quest Board pill — pinned bottom-LEFT (opposite end
          of the Book Club pill which lives bottom-right). User spec
          Feb 26 2026: "Push quest to the opposite end of book club and pin". */}
      <button
        type="button"
        onClick={() => navigate('/quests')}
        className="fixed bottom-40 left-4 z-40 px-4 py-3 rounded-full shadow-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-sm flex items-center gap-2 transition-all"
        data-testid="discover-quest-pill"
      >
        <Trophy className="w-4 h-4" /> Quests
      </button>

      {/* Floating Book Club pill (Feb 26 2026 — user spec "Let these live
          in their own separate pill and page"). Anchored bottom-right
          ABOVE the mini-player & voice button so it's always reachable
          without colliding with other UI. */}
      <button
        type="button"
        onClick={() => navigate('/club')}
        className="fixed bottom-40 right-4 z-40 px-4 py-3 rounded-full shadow-lg bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-sm flex items-center gap-2 transition-all"
        data-testid="discover-bookclub-pill"
      >
        <MessageSquare className="w-4 h-4" /> Book Club
      </button>
    </div>
  );
};

export default Discover;
